import React, { ErrorInfo, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { analyticsService } from '../services/analyticsService';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    try {
      analyticsService.logError(error, { componentStack: errorInfo.componentStack });
    } catch (e) {
      console.error('Failed to log error to analytics', e);
    }

    // Auto-redirect to home after 4 seconds so Google never indexes this page
    setTimeout(() => {
      window.location.href = '/';
    }, 4000);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FF5A00] flex items-center justify-center p-4">
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <div className="max-w-sm w-full text-center">
            <div className="text-white mb-6">
              <svg
                className="w-20 h-20 mx-auto mb-4 opacity-80"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
              <h1 className="text-3xl font-black mb-2">Oops!</h1>
              <p className="text-white/80 font-medium mb-1">
                Something went wrong. Returning you to Pambo...
              </p>
              <p className="text-white/60 text-sm">You'll be redirected in a moment.</p>
            </div>

            <button
              onClick={() => (window.location.href = '/')}
              className="bg-white text-[#FF5A00] font-black py-3 px-8 rounded-2xl hover:bg-orange-50 transition shadow-lg"
            >
              Go to Pambo Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
